/**
 * GhostDetailPanel — bubble-detail panel shown BELOW IndustryPipelineOverview
 * when an orphan-ghost bubble is clicked in Asset View.
 *
 * Semantic: an NA-UC ghost aggregates the portion of a UC's pipeline that is
 * NOT covered by any asset-level SFDC tag (see IndustryPipelineOverview's
 * two-tier ghost logic). Clicking one drills into "which specific opps are
 * missing an asset tag", so we fetch the whole UC's opp population and filter
 * client-side to opps with an empty `assetIds` list. Header + KPI grid mirror
 * the shape of UseCaseDetailPanel / AssetDetailPanel so operators keep one
 * mental model for detail panels.
 */

import type { GhostBubble } from './PipelineMap';
import {
  KPI_INFO_TIPS,
  PipelineDetailShell,
  StatCell,
  deriveKpisFromOpps,
  launchRatioLabel,
} from './PipelineDetailShell';

export default function GhostDetailPanel({
  bubble,
  pipelineTypeFilter,
  pipelineFilter,
  snapshotDate,
  onClose,
}: {
  bubble: GhostBubble;
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded so the drill-in matches the bubble filter. */
  pipelineFilter?: string;
  /** Same snapshotDate driving the chart above. */
  snapshotDate?: string;
  onClose: () => void;
}) {
  const { industry, ucName } = bubble;

  return (
    <PipelineDetailShell
      title={`NA- ${ucName}`}
      subtitle={`${industry} · orphan opps (no asset tag)`}
      headerExtra={
        <span
          className="ivt-ucdp__badge"
          style={{ background: '#F1F5F9', color: '#64748B' }}
        >
          Orphan
        </span>
      }
      fetch={{ industry, ucName }}
      pipelineTypeFilter={pipelineTypeFilter}
      pipelineFilter={pipelineFilter}
      snapshotDate={snapshotDate}
      oppFilter={(opp) => (opp.assetIds ?? []).length === 0}
      showUcNameInStages={false}
      loadingLabel={`加载 ${ucName} orphan 明细…`}
      errorHeader="无法加载 orphan 明细"
      onClose={onClose}
      renderKpis={(detail) => {
        // Roll up from the orphan-scoped opps only (industry / UC stageInsight
        // would include tagged opps too, so we recompute against the filtered
        // set). Matches AccountDetailPanel's pattern.
        const opps = detail
          ? (detail.stages ?? []).flatMap((s) =>
              (s.opps ?? []).filter((o) => (o.assetIds ?? []).length === 0),
            )
          : [];
        const kpis = detail
          ? deriveKpisFromOpps(opps)
          : {
              stronglyTouchedOpps: '—' as const,
              touchedOpps: '—' as const,
              penetrationPct: '—' as const,
              pipelineDepth: '—' as const,
            };
        // Top-half KPIs from the SAME orphan-scoped opps so filter changes
        // (which re-fetch and re-narrow) flow through. Reading from the
        // ghost bubble snapshot would show stale values.
        const totalArr = opps.reduce((n, o) => n + (o.totalArr ?? 0), 0);
        const launchedArr = opps.reduce(
          (n, o) => n + (o.oppStatus === 'launched' ? (o.totalArr ?? 0) : 0),
          0,
        );
        const arrM = detail ? (totalArr / 1e6).toFixed(2) : '—';
        return (
          <>
            <StatCell label="Industry" value={industry} />
            <StatCell label="Opps" value={detail ? opps.length : '—'} />
            <StatCell label="ARR" value={detail ? `$${arrM}M` : '—'} />
            <StatCell
              label="Launch Ratio"
              value={detail ? launchRatioLabel(launchedArr, totalArr) : '—'}
            />
            <StatCell
              label="Pipeline平均参与深度"
              value={kpis.pipelineDepth ?? '—'}
              info={KPI_INFO_TIPS.pipelineDepth}
            />
            <StatCell
              label="渗透率"
              value={kpis.penetrationPct}
              info={KPI_INFO_TIPS.penetration}
            />
            <StatCell
              label="强相关opps数量"
              value={kpis.stronglyTouchedOpps}
              info={KPI_INFO_TIPS.stronglyTouchedOpps}
            />
            <StatCell
              label="IVT相关opps数量"
              value={kpis.touchedOpps}
              info={KPI_INFO_TIPS.touchedOpps}
            />
          </>
        );
      }}
    />
  );
}
